import type { PackTerms, PackBalances, PackHistoryEntry } from '../types';

type PackPresentation = PackTerms & {
  kind: 'pack' | 'unlimited';
  status?: 'active' | 'cancelled';
  creditsRemaining: number | null;
  expiresAt: string;
  balances?: PackBalances | null;
  history?: PackHistoryEntry[];
};

export function packStatus(pack: PackPresentation, now = Date.now()): string {
  if (pack.status === 'cancelled') return 'Cancelado';
  if (Date.parse(pack.expiresAt) <= now) return 'Vencido';
  if (pack.kind === 'unlimited' || (pack.creditsRemaining ?? 0) > 0) return 'Activo';
  return pack.balances && (pack.balances.held > 0 || pack.balances.booked > 0) ? 'En uso' : 'Agotado';
}

export function packBalanceRows(pack: PackPresentation) {
  if (pack.kind === 'unlimited' || !pack.balances) return [];
  const { available, held, booked, used, expired } = pack.balances;
  return [
    { label: 'Disponibles', value: available }, { label: 'Apartados', value: held },
    { label: 'Reservados', value: booked }, { label: 'Usados', value: used },
    { label: 'Vencidos', value: expired },
  ];
}

export function packHistory(pack: PackPresentation): PackHistoryEntry[] {
  return [...(pack.history ?? [])].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
}

export function packEligibility<T extends { id: string; name: string }>(pack: PackTerms, services: readonly T[]): string[] {
  const ids = pack.eligibleServiceIds;
  if (ids == null) return ['Todos los servicios del negocio'];
  if (ids.length === 0) return ['Sin servicios habilitados'];
  return ids.map(id => services.find(service => service.id === id)?.name ?? 'Servicio fuera del catálogo actual');
}

export function packCancellation(pack: PackTerms): string {
  const policy = pack.policySnapshot;
  if (!policy) return 'Cancela antes del inicio para devolver el crédito. Desde el inicio, se consume. Si no asistes, se consume el crédito.';
  const cancellation = policy.lateCancellationConsumesCredit
    ? `Cancela al menos ${policy.cutoffMinutes} minutos antes del inicio para devolver el crédito. Después de ese plazo, se consume.`
    : 'Si cancelas, se devuelve el crédito, incluso después del plazo de cancelación.';
  const noShow = policy.noShowConsumesCredit ? 'Si no asistes, se consume el crédito.' : 'Si no asistes, se devuelve el crédito.';
  return `${cancellation} ${noShow}`;
}

export const PACK_EVENTS: Record<PackHistoryEntry['event'], string> = {
  opening: 'Saldo inicial registrado', reserve: 'Crédito apartado', book: 'Reserva confirmada',
  use: 'Clase utilizada', release: 'Apartado liberado', cancel: 'Reserva cancelada',
  no_show: 'Inasistencia registrada', expire: 'Créditos vencidos', adjustment: 'Ajuste del negocio',
};

export function packLedgerBalanceLabel(pack: PackPresentation, balances: PackPresentation['balances']): string {
  if (pack.kind === 'unlimited') return 'Pase ilimitado';
  if (!balances) return 'Desglose no disponible';
  return `${balances.available} disponibles · ${balances.held} apartados · ${balances.booked} reservados · ${balances.used} usados · ${balances.expired} vencidos`;
}
